import { useThree } from '@react-three/fiber';
import { useCallback, useEffect, useRef } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { hasKeyPoseBoneSelection } from '../keyPoseSelectors';
import { useStore as useKeyPoseStore } from '../keyPoseStore';
import { shouldSuppressKeyPoseCanvasDeselect } from './keyPoseEditingDeselectUtils';

const CANVAS_CLICK_DESELECT_MOVE_THRESHOLD_PX = 5;

interface PointerState {
  pointerId: number;
  clientX: number;
  clientY: number;
  moved: boolean;
}

export function useKeyPoseEditingDeselect() {
  const keyPoseStore = useKeyPoseStore();
  const gl = useThree((state) => state.gl);
  const canvasPointerRef = useRef<PointerState | null>(null);
  const suppressNextCanvasDeselectRef = useRef(false);

  const clearKeyPoseEditingSelection = useCallback(() => {
    return keyPoseStore.clearKeyPoseEditingSelection();
  }, [keyPoseStore]);

  const onHandleDragStart = useCallback(() => {
    suppressNextCanvasDeselectRef.current = true;
  }, []);

  useEffect(() => {
    const canvas = gl.domElement;

    const handlePointerDown = (event: PointerEvent) => {
      canvasPointerRef.current = {
        pointerId: event.pointerId,
        clientX: event.clientX,
        clientY: event.clientY,
        moved: false,
      };
    };

    const handlePointerMove = (event: PointerEvent) => {
      const pointer = canvasPointerRef.current;
      if (!pointer || pointer.pointerId !== event.pointerId) {
        return;
      }

      const deltaX = event.clientX - pointer.clientX;
      const deltaY = event.clientY - pointer.clientY;
      const moveThresholdSquared =
        CANVAS_CLICK_DESELECT_MOVE_THRESHOLD_PX *
        CANVAS_CLICK_DESELECT_MOVE_THRESHOLD_PX;
      if (deltaX * deltaX + deltaY * deltaY > moveThresholdSquared) {
        pointer.moved = true;
      }
    };

    const handlePointerUp = (event: PointerEvent) => {
      const pointer = canvasPointerRef.current;
      canvasPointerRef.current = null;

      if (!pointer || pointer.pointerId !== event.pointerId) {
        suppressNextCanvasDeselectRef.current = false;
        return;
      }

      const suppressDeselect = suppressNextCanvasDeselectRef.current;
      suppressNextCanvasDeselectRef.current = false;
      if (
        shouldSuppressKeyPoseCanvasDeselect({
          dragging: keyPoseStore.state.editing.dragging,
          event,
          pointerMoved: pointer.moved,
          suppressDeselect,
        })
      ) {
        return;
      }

      clearKeyPoseEditingSelection();
    };

    canvas.addEventListener('pointerdown', handlePointerDown);
    canvas.addEventListener('pointermove', handlePointerMove);
    canvas.addEventListener('pointerup', handlePointerUp);

    return () => {
      canvas.removeEventListener('pointerdown', handlePointerDown);
      canvas.removeEventListener('pointermove', handlePointerMove);
      canvas.removeEventListener('pointerup', handlePointerUp);
    };
  }, [clearKeyPoseEditingSelection, gl.domElement, keyPoseStore]);

  useHotkeys(
    'escape, q',
    () => {
      clearKeyPoseEditingSelection();
    },
    {
      preventDefault: true,
      ignoreEventWhen: shouldIgnoreKeyPoseEditingHotkey,
      enabled: hasKeyPoseBoneSelection(keyPoseStore.state.editing),
    },
    [clearKeyPoseEditingSelection],
  );

  return { onHandleDragStart };
}

function shouldIgnoreKeyPoseEditingHotkey(event: KeyboardEvent) {
  const target = event.target as HTMLElement | null;
  if (!target?.tagName) {
    return false;
  }

  const tagName = target.tagName.toUpperCase();
  return (
    tagName === 'INPUT' ||
    tagName === 'TEXTAREA' ||
    tagName === 'SELECT' ||
    target.isContentEditable
  );
}
